import type { Blockchain } from "@anaconda/core/blockchain/types"
import { Blockchain as BlockchainEnum } from "@anaconda/core/blockchain/types"
import type { PriceValue } from "@anaconda/core/price/types"
import { useQuery } from "@tanstack/react-query"
import { useMolecule } from "bunshi/react"
import { useMemo } from "react"
import { EvmFeesMoleculeReatom } from "../evm/evm-fees-molecule.reatom"
import { SolanaFeesMoleculeReatom } from "../solana/solana-fees-molecule.reatom"

// We output it in percentge instead of basis points, but we use basis points in SDK
export function useEvmServiceFee(blockchain: Blockchain, price?: PriceValue) {
  const { reatomModernFeeQuery } = useMolecule(EvmFeesMoleculeReatom)
  const args = useMemo(() => ({ price, blockchain }), [price, blockchain])
  const queryParams = reatomModernFeeQuery(args)()
  const isEvm = blockchain !== BlockchainEnum.SOLANA
  const result = useQuery({ ...queryParams, enabled: isEvm })

  const query = useMemo(() => {
    const newResult = {
      ...result,
      data: result.data ? result.data.sellerBps / 100 : undefined,
    }
    return newResult
  }, [result])

  return query
}

export function useSolanaServiceFee(
  blockchain: Blockchain,
  price?: PriceValue,
) {
  const { reatomSolanaServiceFeeQuery } = useMolecule(SolanaFeesMoleculeReatom)
  const args = useMemo(() => ({ price, blockchain }), [price, blockchain])
  const queryParams = reatomSolanaServiceFeeQuery(args)()
  const isSolana = blockchain === BlockchainEnum.SOLANA
  return useQuery({ ...queryParams, enabled: isSolana })
}

export function useServiceFee(blockchain: Blockchain, price?: PriceValue) {
  const evm = useEvmServiceFee(blockchain, price)
  const solana = useSolanaServiceFee(blockchain, price)
  const isSolana = blockchain === BlockchainEnum.SOLANA
  return isSolana ? solana : evm
}
